import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Dialog,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";
import { useTranslation } from "react-i18next";
import { CustomerAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { ReturnViewModel, ListQuery } from "../../../Types/ExportTypes";
import { buildQueryFilter } from "../../../Utils/QueryFilterUtils";
import LoadingSection from "../../../components/LoadingSection";
import useCachedListQuery from "../../../hooks/useCachedListQuery";
import ResponsivePagination from "../../../components/ResponsivePagination";

const ReturnList: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { open = true } = params || {};
    const [documents, setDocuments] = useState<ReturnViewModel[]>([]);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    }, `returnList_${open}`);
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();



    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: ReturnViewModel[]; count: number }>(
                `/salesReturnApi/GetReturns?open=${open}`,
                query
            );
            setDocuments(response.items);
            setTotalDocuments(response.count);
        } catch (error: any) {
            console.error("Failed to fetch documents:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };

    const handleSelectDocument = async (documentNo: string) => {
        navigate({ path: "sales/return/edit", params: { returnNo: documentNo, history: open == "false" } });
    };




    return (
        <>
            <CenteredListCard
                title={t("common.returns")

                }
            >


                    {/* Search Field */}
                    <Box
                        component="form"
                        onSubmit={handleSearchSubmit}
                        sx={{
                            display: "flex",
                            gap: 2,
                            marginBottom: 2,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={5} lg={2}>
                                <TextField
                                    select
                                    label={t("common.field")}
                                    value={filterProperty}
                                    onChange={(e) => setFilterProperty(e.target.value)}
                                    SelectProps={{ native: true }}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                >
                                    <option value="">{t("common.default")}</option>
                                    <option value="ext_No">{t("common.number")}</option>
                                    <option value="ext_OrderNo">{t("common.invoiceNo")}</option>
                                    <option value="ext_CustomerNo">{t("common.customerNo")}</option>
                                    <option value="ext_CustomerEmail">{t("common.email")}</option>
                                </TextField>
                            </Grid>
                            <Grid item xs={7} lg={7}>
                                {filterProperty === "ext_CustomerNo" ? (
                                    <CustomerAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                ) : (
                                    <TextField
                                        label={t("common.search")}
                                        variant="outlined"
                                        fullWidth
                                        onChange={(event) => setFilter(event.target.value)}
                                        value={filter}
                                        disabled={loading}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} lg={3}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {t("common.search")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                        {query.filters?.map((f, index) => (
                            <Chip
                                key={index}
                                label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                                onDelete={() => {
                                    const newFilters = query.filters?.filter((_, i) => i !== index);
                                    setQuery({ ...query, filters: newFilters, page: 0 });
                                }}
                                deleteIcon={<DeleteIcon />}
                            />
                        ))}
                    </Box>

                    {/* Card Layout for Orders */}
                    <LoadingSection loading={loading}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {documents.map((document) => (
                                <Grid item
                                    xs={12}
                                    sm={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 6}
                                    md={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 4}
                                    key={document.returnNo}>
                                    <Card
                                        onClick={() => handleSelectDocument(document?.returnNo ?? "")}
                                        sx={{
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 4,
                                                backgroundColor: "secondary.main",
                                            },
                                        }}
                                    >
                                        <CardContent>
                                            <ListCardHeader
                                                icon={<AssignmentReturnIcon fontSize="small" color="action" />}
                                                title={`${document.returnNo} - ${document.invoiceNo}${document.orderNo && document.orderNo.length > 0 ? ` - ${document.orderNo}` : ""}`}
                                            />
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.customerNo")}: {document.returnCustomer}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.name")}: {document.returnName}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.address")}:{document.returnAdress}
                                            </Typography>
                                            {open == "true" && (
                                                <Typography variant="body2" color="textSecondary">
                                                    {t("common.returnStatus")}: {t("common.returnStatus_" + (document.status ?? ""))}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                            {documents.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography align="center">{t("common.noEntriesFound")}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </LoadingSection>

                    {/* Pagination */}
                    <ResponsivePagination

                        page={query.page}

                        pageSize={query.pageSize}

                        totalCount={totalDocuments}

                        onPageChange={handlePageChange}

                    />
          
            </CenteredListCard>
        </>
    );
};

export default ReturnList;